/**
 * Covering Raw Material Reconciliation (READ-ONLY, department-level).
 * Report 2 — common operational RM pool. Served by
 * GET /api/covering-reconciliation/raw-material. No CWO selector. No writes.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CubeIcon, ArrowsClockwiseIcon, PrinterIcon, CaretRightIcon, CaretDownIcon } from "@phosphor-icons/react";

const kg = (n) => (n === null || n === undefined) ? "—" : `${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg`;
const STATUS_CLS = (s) => {
  const u = (s || "").toUpperCase();
  if (u === "RECONCILED") return "bg-emerald-500/15 text-emerald-300 border-emerald-500/40";
  if (u === "SHORTAGE") return "bg-red-500/15 text-red-300 border-red-500/40";
  if (u === "EXCESS") return "bg-sky-500/15 text-sky-300 border-sky-500/40";
  if (u.includes("PENDING") || u.includes("PHY VER")) return "bg-amber-500/15 text-amber-300 border-amber-500/40";
  if (u === "VARIANCE") return "bg-orange-500/15 text-orange-300 border-orange-500/40";
  return "bg-zinc-500/15 text-zinc-300 border-zinc-500/40";
};
const Code = ({ children }) => <span className="font-mono text-[11px] text-white break-all whitespace-normal leading-snug">{children}</span>;
const Num = ({ children, cls = "" }) => <span className={`font-mono tabular-nums ${cls}`}>{children}</span>;
const Badge = ({ s }) => <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border whitespace-nowrap ${STATUS_CLS(s)}`}>{s}</span>;
const Section = ({ title, note, children, testId }) => (
  <div className="tactical-card overflow-hidden" data-testid={testId}>
    <div className="px-5 py-3 border-b border-[var(--border)] bg-[var(--bg-2)]/30">
      <div className="text-sm font-semibold text-white uppercase tracking-wide">{title}</div>
      {note && <div className="text-[10px] text-zinc-500 mt-1 leading-relaxed max-w-4xl">{note}</div>}
    </div>
    <div className="overflow-x-auto">{children}</div>
  </div>
);

export default function CoveringRawMaterialReconciliation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rmFilter, setRmFilter] = useState("");
  const [open, setOpen] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/covering-reconciliation/raw-material", { params: rmFilter ? { rm_item: rmFilter } : {} });
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load Covering RM reconciliation"); setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const H = data?.header;
  const allCodes = (data?.section_a?.rows || []).map(r => r.rm_code);

  return (
    <div className="p-6 space-y-5" data-testid="covering-rm-recon-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><CubeIcon size={11} weight="bold" />Reports · Reconciliation</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Covering Raw Material Reconciliation</h1>
          <div className="text-xs text-zinc-500 mt-1">Department-level common RM pool — what entered Covering, how much was consumed across all Covering CWOs, and what should remain. Read-only.</div>
        </div>
        <div className="flex items-center gap-2">
          {data && <button onClick={() => window.print()} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2" data-testid="cov-rm-print"><PrinterIcon size={14} weight="bold" />Print</button>}
          <button onClick={load} disabled={loading} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2 disabled:opacity-40" data-testid="cov-rm-refresh"><ArrowsClockwiseIcon size={14} weight="bold" />Refresh</button>
        </div>
      </div>

      {H && (
        <div className="tactical-card p-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-[11px] font-mono" data-testid="cov-rm-header">
          <div><div className="text-zinc-500 mb-0.5 uppercase tracking-widest text-[9px]">Department</div><div className="text-white">{H.department}</div></div>
          <div><div className="text-zinc-500 mb-0.5 uppercase tracking-widest text-[9px]">Location / Warehouse</div><div className="text-white break-all">{H.location || "—"}</div></div>
          <div className="md:col-span-1"><div className="text-zinc-500 mb-0.5 uppercase tracking-widest text-[9px]">Reconciliation As-Of</div><div className="text-white">{(H.as_of || "").replace("T", " ").slice(0, 19)}</div></div>
          <div>
            <div className="text-zinc-500 mb-0.5 uppercase tracking-widest text-[9px]">Filter · RM Item</div>
            <select value={rmFilter} onChange={e => setRmFilter(e.target.value)} data-testid="cov-rm-filter"
                    className="h-8 px-2 w-full bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-[11px]">
              <option value="">All RM Items</option>
              {allCodes.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      )}

      {loading && <div className="tactical-card p-8 text-center text-zinc-500">Loading reconciliation…</div>}

      {data && (
        <>
          <Section title="A · Covering RM Material Reconciliation" testId="cov-rm-section-a"
                   note="Book Balance = Total Received − Total Consumption. Total Received = actual receipts into Covering (NOT CWO MI / BOM / requirement). Total Consumption = all posted Covering RM consumption across every applicable CWO (common pool). Expand a row for the CWO consumption breakdown.">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]"><tr>
                <th className="text-left px-4 py-2 w-6"></th><th className="text-left px-4 py-2">RM Item</th><th className="text-right px-4 py-2">Total Received</th><th className="text-right px-4 py-2">Total Consumption</th><th className="text-right px-4 py-2">Book Balance</th><th className="text-right px-4 py-2">Stock V2</th><th className="text-right px-4 py-2">Physical / Returned</th><th className="text-right px-4 py-2">Variance</th><th className="text-left px-4 py-2 pl-6">Status</th>
              </tr></thead>
              <tbody>
                {data.section_a.rows.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-zinc-500">No Covering RM movements found</td></tr>}
                {data.section_a.rows.map((r, i) => (
                  <>
                    <tr key={i} className="border-b border-[var(--border)]/60 cursor-pointer hover:bg-[var(--bg-2)]/30 align-top" onClick={() => setOpen(o => ({ ...o, [r.rm_code]: !o[r.rm_code] }))} data-testid={`cov-rm-row-${i}`}>
                      <td className="px-4 py-2 text-zinc-400">{(r.drill_down || []).length > 0 ? (open[r.rm_code] ? <CaretDownIcon size={12} /> : <CaretRightIcon size={12} />) : null}</td>
                      <td className="px-4 py-2 max-w-[360px]"><Code>{r.rm_code}</Code></td>
                      <td className="px-4 py-2 text-right"><Num cls="text-emerald-300">{kg(r.total_received)}</Num></td>
                      <td className="px-4 py-2 text-right"><Num cls="text-purple-300">{kg(r.total_consumption)}</Num></td>
                      <td className="px-4 py-2 text-right"><Num cls="text-amber-300">{kg(r.book_balance)}</Num></td>
                      <td className="px-4 py-2 text-right"><Num cls="text-sky-300">{kg(r.v2_balance)}</Num></td>
                      <td className="px-4 py-2 text-right text-zinc-500">{r.physical_returned === null ? "—" : kg(r.physical_returned)}</td>
                      <td className="px-4 py-2 text-right text-zinc-500">{r.variance === null ? "—" : kg(r.variance)}</td>
                      <td className="px-4 py-2 pl-6"><Badge s={r.status} /></td>
                    </tr>
                    {open[r.rm_code] && (
                      <tr className="bg-[var(--bg-0)]/40 border-b border-[var(--border)]/30" data-testid={`cov-rm-drill-${i}`}>
                        <td></td>
                        <td colSpan={8} className="px-4 py-2">
                          <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Consumption Breakdown (common pool · cross-WO permitted)</div>
                          <table className="w-full text-[11px]">
                            <thead className="text-zinc-500"><tr><th className="text-left py-1">CWO No</th><th className="text-left py-1">Parent WO</th><th className="text-left py-1">SO / ESO</th><th className="text-left py-1">Product Code</th><th className="text-left py-1">Posting Ref</th><th className="text-right py-1">Consumed</th></tr></thead>
                            <tbody>
                              {r.drill_down.map((d, j) => (
                                <tr key={j} className="border-t border-[var(--border)]/30">
                                  <td className="py-1 font-mono text-white">{d.cwo_no}</td>
                                  <td className="py-1 font-mono text-zinc-300">{d.parent_wo_no || "—"}</td>
                                  <td className="py-1 font-mono text-zinc-300">{d.so_eso_no || "—"}</td>
                                  <td className="py-1 font-mono text-zinc-300 break-all max-w-[260px]">{d.product_code || "—"}</td>
                                  <td className="py-1 font-mono text-zinc-400">{d.posting_ref || "—"}</td>
                                  <td className="py-1 text-right"><Num>{kg(d.consumed)}</Num></td>
                                </tr>
                              ))}
                              <tr className="border-t border-[var(--border)] font-semibold text-white"><td className="py-1" colSpan={5}>TOTAL</td><td className="py-1 text-right"><Num>{kg(r.total_consumption)}</Num></td></tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="B · Reconciliation Control & Exceptions" testId="cov-rm-section-b"
                   note="All applicable control rows (including zero-variance RECONCILED). Common-pool / cross-WO consumption is not a defect. Reporting only — nothing is auto-corrected.">
            <table className="w-full text-sm table-fixed">
              <colgroup><col style={{ width: "40%" }} /><col style={{ width: "23%" }} /><col style={{ width: "8%" }} /><col style={{ width: "8%" }} /><col style={{ width: "8%" }} /><col style={{ width: "13%" }} /></colgroup>
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]"><tr>
                <th className="text-left px-4 py-2">Item</th><th className="text-left px-4 py-2">Control Type</th><th className="text-right px-4 py-2">Expected</th><th className="text-right px-4 py-2">Actual</th><th className="text-right px-4 py-2">Variance</th><th className="text-left px-4 py-2">Status</th>
              </tr></thead>
              <tbody>
                {data.section_b.rows.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-zinc-500">No control rows</td></tr>}
                {data.section_b.rows.map((r, i) => (
                  <tr key={i} className="border-b border-[var(--border)]/50 align-top" data-testid={`cov-rm-ctrl-${i}`}>
                    <td className="px-4 py-2 text-left"><Code>{r.item}</Code></td>
                    <td className="px-4 py-2 text-left"><span className="text-[11px] text-zinc-300 break-words leading-snug">{r.control}</span></td>
                    <td className="px-4 py-2 text-right text-zinc-400">{r.expected === null ? "—" : kg(r.expected)}</td>
                    <td className="px-4 py-2 text-right text-zinc-400">{r.actual === null ? "—" : kg(r.actual)}</td>
                    <td className="px-4 py-2 text-right text-zinc-400">{r.variance === null ? "—" : kg(r.variance)}</td>
                    <td className="px-4 py-2 text-left"><Badge s={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </>
      )}
    </div>
  );
}
